import type { Probe, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const SPECTRE = 'https://spectreattack.org/';

/**
 * Infrastructure timing substrate (security-research §7, KPTI idea).
 *
 * WHAT THIS IS: the measurement half of a server-side infrastructure
 * fingerprint. The KPTI / syscall-overhead idea says that the latency
 * distribution of rapid small requests differs between a patched cloud kernel,
 * a bare-metal residential connection, and a hypervisor with its own overhead —
 * and that difference could help tell a "residential" exit that is really a
 * cloud farm.
 *
 * WHAT THIS IS NOT: a verdict. We deliberately do NOT infer "patched cloud
 * kernel" from a latency distribution, because that needs a per-host historical
 * baseline gathered over many visits, and this project does not accumulate
 * cross-visitor history (it is a privacy-scoped research tool). Shipping a
 * confident-looking verdict from a single sample would be exactly the kind of
 * unsupported claim this catalog exists to avoid.
 *
 * What we do report is the raw, correctly-shaped substrate: a latency
 * distribution (mean / median / p95 / stddev) over N rapid small requests
 * against a caller-configured endpoint, plus a coefficient-of-variation figure
 * that a downstream correlator could use.
 *
 * The endpoint is caller-supplied (VITE_INFRA_ENDPOINT). With no endpoint
 * configured — the default, and the only state the deployed site can be in —
 * the probe returns no signals at all rather than a constant "not exercised"
 * placeholder, and contributes nothing to the fingerprint.
 */
export const infraTimingProbe: Probe = {
  id: 'infraTiming',
  label: 'Infrastructure latency distribution',
  layer: 'network',
  tier: 'server',
  category: 'network',
  description: 'Raw latency distribution over rapid small requests (KPTI/farm signal substrate)',
  sources: [SPECTRE],
  budgetMs: 15_000,

  async run(): Promise<ProbeResult> {
    const endpoint = (import.meta.env?.VITE_INFRA_ENDPOINT as string | undefined) ?? null;

    if (!endpoint) {
      // Stay silent rather than emit a constant "not exercised" trio. On the
      // static deployment this can never be configured, so those signals were
      // pure clutter; the catalog entry explains the limitation.
      return { signals: {} };
    }

    // Rapid small requests: the workload the KPTI idea describes.
    const N = 20;
    const samples: number[] = [];
    for (let i = 0; i < N; i++) {
      const t0 = performance.now();
      try {
        const res = await fetch(`${endpoint}?i=${i}&t=${Date.now()}`, {
          cache: 'no-store',
          // Keep the response tiny; we only care about the round trip.
          headers: { accept: '*/*' },
        });
        await res.arrayBuffer();
        samples.push(performance.now() - t0);
      } catch {
        // A failed request is not a latency sample; skip it.
      }
    }

    if (samples.length < 5) {
      return {
        signals: {
          available: ok(false, 'derived'),
          exercised: ok(true, 'derived'),
          sampleCount: ok(samples.length, 'client'),
          reason: ok('Too few successful samples to characterise a distribution.', 'client'),
        },
      };
    }

    const sorted = [...samples].sort((a, b) => a - b);
    const mean = samples.reduce((a, b) => a + b, 0) / samples.length;
    const median = sorted[Math.floor(sorted.length / 2)];
    const p95 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))];
    const stddev = Math.sqrt(
      samples.reduce((a, b) => a + (b - mean) ** 2, 0) / samples.length,
    );
    // Coefficient of variation: a scale-free dispersion figure that a
    // downstream correlator (cloud vs bare metal) would key on.
    const cv = mean > 0 ? stddev / mean : 0;

    return {
      signals: {
        available: ok(true, 'derived'),
        exercised: ok(true, 'derived'),
        sampleCount: ok(samples.length, 'client'),
        meanMs: ok(sig(mean, 3), 'derived'),
        medianMs: ok(sig(median, 3), 'derived'),
        p95Ms: ok(sig(p95, 3), 'derived'),
        stddevMs: ok(sig(stddev, 3), 'derived'),
        coefficientOfVariation: ok(sig(cv, 4), 'derived'),
        minMs: ok(sig(sorted[0], 3), 'derived'),
        maxMs: ok(sig(sorted[sorted.length - 1], 3), 'derived'),
        verdict: ok(
          'none — raw distribution only; inferring a kernel/virtualisation class needs a per-host baseline this project does not keep.',
          'derived',
        ),
      },
      // Deliberately NOT added to any fingerprint part. A latency distribution
      // is environment- and moment-dependent; feeding it into the fingerprint
      // would make the hash drift with network conditions.
    };
  },
};

registerProbe(infraTimingProbe);
